import {
  CartItemPayload,
  CartUpdateItemReqBody,
  CartUpdatePaymentItemsReqBody,
} from '@sola/schema/dotcom/cart/api.ts';
import { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import { Cart, CartUpsellActionEnum } from '@sola/schema/dotcom/cart/cart.ts';
import { CartItemPaymentPayload } from '@sola/schema/dotcom/cart/payment.ts';
import {
  GetOptions,
  httpDelete,
  httpGet,
  httpPatch,
  httpPost,
} from '@sola/shared-utils/httpClient.ts';

import { CartQueryParam } from './queryparams.ts';

const cartApiBase =
  process.env.CART_API_PROXY_DESTINATION ||
  process.env.NEXT_PUBLIC_BASE_URL_CART ||
  process.env.BASE_URL_CART ||
  'http://localhost:3000';

const baseApiResource = `${cartApiBase}/carts`;
const defaultOptions: GetOptions = {
  credentials: 'include',
};

/**
 * Get cart request
 */
export const getCartRequest = async (
  cartId?: string,
  queryParams?: Record<string, string>,
) => {
  return await httpGet<Cart>(
    cartId ? `${baseApiResource}/${cartId}` : baseApiResource,
    { ...defaultOptions, queryParams: new URLSearchParams(queryParams) },
  );
};

/**
 * Add items POST request.
 */
export const addItemsRequest = async (
  items: CartItemPayload[],
  supporterId?: string,
  referrer?: string,
) => {
  return await httpPost<Cart>(
    `${baseApiResource}/items${referrer ? `?referrer=${referrer}` : ''}`,
    { items, supporterId },
    {
      ...defaultOptions,
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
};

/**
 * Remove items DELETE request.
 */
export const removeItemRequest = async (id: CartItem['id'], cartId = '') => {
  const idPath = cartId ? `/${cartId}` : '';
  return await httpDelete<Cart>(
    `${baseApiResource}${idPath}/items/${id}`,
    defaultOptions,
  );
};

/**
 * Update item PATCH request.
 */
export const updateItemRequest = async (
  id: CartItem['id'],
  updates: CartUpdateItemReqBody,
) => {
  return await httpPatch<Cart>(
    `${baseApiResource}/items/${id}`,
    updates,
    defaultOptions,
  );
};

/**
 * Update payment items POST request.
 */
export const updatePaymentItemsRequest = async (
  paymentItems: CartItemPaymentPayload[],
  cartId = '',
) => {
  const idPath = cartId ? `/${cartId}` : '';
  const body: CartUpdatePaymentItemsReqBody = {
    paymentItems,
  };

  return await httpPost<Cart>(
    `${baseApiResource}${idPath}/payment-items`,
    body,
    {
      ...defaultOptions,
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
};

/**
 * Increment the cart's Upsell `accept` or `decline` count
 */
export const updateUpsellRequest = async ({
  action,
  itemId,
}: {
  action: CartUpsellActionEnum;
  itemId?: string;
}) => {
  return await httpPatch<Cart>(
    `${baseApiResource}/upsell`,
    { action, itemId },
    {
      ...defaultOptions,
    },
  );
};

/**
 * Logout delete request
 */
export const logoutRequest = async () => {
  return await httpDelete<Cart>(`${baseApiResource}/logout`, defaultOptions);
};

/**
 * Get restored cart request
 */
export const getRestoredCartRequest = async (restoreCartId: string) => {
  return await httpGet<Cart>(
    `${baseApiResource}/restore?${CartQueryParam.RestoreCartId}=${restoreCartId}`,
    defaultOptions,
  );
};
